/**
 * RoleComparisonPanel — Side-by-side multi-role lens comparison (DRR-103).
 *
 * Shows field-level access differences across roles (ALD vs CNA vs DON).
 * Three sections: universally hidden, universally visible, mixed access.
 *
 * Click-to-expand: role cards show full scope + hidden field lists,
 * mixed-access rows expand to show which roles hide/see the field.
 *
 * Backend-driven: all data comes from compareLensEvaluation query.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Badge,
  Collapse,
  Divider,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconChevronDown,
  IconChevronRight,
  IconEye,
  IconEyeOff,
  IconShieldCheck,
  IconShieldX,
} from '@tabler/icons-react';

import type { LensComparisonResult } from './hooks/useCompareLensEvaluation';

// ============================================================================
// Props
// ============================================================================

export interface RoleComparisonPanelProps {
  /** Comparison data from useCompareLensEvaluation. */
  comparison: LensComparisonResult;
}

// ============================================================================
// Component
// ============================================================================

export function RoleComparisonPanel({ comparison }: RoleComparisonPanelProps) {
  const { roles, universallyHidden, universallyVisible, mixedAccessFields } =
    comparison;

  // Expanded state: role cards and field rows
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleRole = useCallback((label: string) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const toggleField = useCallback((fieldName: string) => {
    setExpandedFields((prev) => {
      const next = new Set(prev);
      if (next.has(fieldName)) next.delete(fieldName);
      else next.add(fieldName);
      return next;
    });
  }, []);

  // Total unique fields across all categories
  const totalFields =
    universallyHidden.length +
    universallyVisible.length +
    mixedAccessFields.length;

  // Role summary cards
  const roleSummaries = useMemo(
    () =>
      roles.map((role) => {
        const visibleCount = totalFields - role.hiddenFieldCount;
        const accessPct =
          totalFields > 0
            ? Math.round((visibleCount / totalFields) * 100)
            : 100;
        return { ...role, visibleCount, accessPct };
      }),
    [roles, totalFields],
  );

  return (
    <Stack gap="md">
      {/* ── Role Summary Cards ──────────────────────────────────────── */}
      <SimpleGrid cols={{ base: 1, sm: roles.length }} spacing="sm">
        {roleSummaries.map((role) => {
          const isExpanded = expandedRoles.has(role.label);
          return (
            <Paper
              key={role.label}
              p="sm"
              withBorder
              style={{ cursor: 'pointer' }}
              onClick={() => toggleRole(role.label)}
            >
              <Group justify="space-between" mb="xs">
                <Group gap={4}>
                  <ThemeIcon variant="subtle" size="xs" color="gray">
                    {isExpanded ? (
                      <IconChevronDown size={12} />
                    ) : (
                      <IconChevronRight size={12} />
                    )}
                  </ThemeIcon>
                  <Text size="sm" fw={700}>
                    {role.label}
                  </Text>
                </Group>
                <Badge
                  size="sm"
                  variant="light"
                  color={role.accessPct > 70 ? 'green' : role.accessPct > 40 ? 'yellow' : 'red'}
                >
                  {role.accessPct}% access
                </Badge>
              </Group>
              <Progress
                value={role.accessPct}
                size="sm"
                color={role.accessPct > 70 ? 'green' : role.accessPct > 40 ? 'yellow' : 'red'}
                mb="xs"
              />
              <Stack gap={2}>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Visible fields</Text>
                  <Text size="xs" fw={500}>{role.visibleCount}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Hidden fields</Text>
                  <Text size="xs" fw={500} c="red">{role.hiddenFieldCount}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">Role IDs</Text>
                  <Group gap={2}>
                    {role.roleIds.map((id) => (
                      <Badge key={id} size="xs" variant="outline" color="gray">
                        {id}
                      </Badge>
                    ))}
                  </Group>
                </Group>
                {role.triggeredRuleId && (
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">Triggered rule</Text>
                    <Text size="xs" style={{ fontFamily: 'monospace' }}>
                      {role.triggeredRuleId}
                    </Text>
                  </Group>
                )}
              </Stack>

              {/* Expanded detail: scopes + hidden field list */}
              <Collapse in={isExpanded}>
                <Divider my="xs" />
                <Text size="xs" fw={600} mb={4}>
                  Scopes ({role.scopes.length})
                </Text>
                <Group gap={4} mb="xs">
                  {role.scopes.map((s) => (
                    <Badge key={s} size="xs" variant="light" color="blue" style={{ fontFamily: 'monospace' }}>
                      {s}
                    </Badge>
                  ))}
                </Group>
                {role.hiddenFields.length > 0 && (
                  <>
                    <Text size="xs" fw={600} mb={4}>
                      Hidden Fields ({role.hiddenFields.length})
                    </Text>
                    <Group gap={4}>
                      {role.hiddenFields.map((f) => (
                        <Badge key={f} size="xs" variant="dot" color="red" style={{ fontFamily: 'monospace' }}>
                          {f}
                        </Badge>
                      ))}
                    </Group>
                  </>
                )}
              </Collapse>
            </Paper>
          );
        })}
      </SimpleGrid>

      {/* ── Mixed Access Fields (most important) ────────────────────── */}
      {mixedAccessFields.length > 0 && (
        <>
          <Divider
            label={
              <Group gap="xs">
                <ThemeIcon variant="light" color="yellow" size="xs">
                  <IconShieldCheck size={12} />
                </ThemeIcon>
                <Text size="sm" fw={600}>
                  Mixed Access ({mixedAccessFields.length} fields)
                </Text>
              </Group>
            }
            labelPosition="left"
          />
          <Paper withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Field</Table.Th>
                  {roles.map((r) => (
                    <Table.Th key={r.label} ta="center">
                      {r.label}
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {mixedAccessFields.map((field) => {
                  const isExpanded = expandedFields.has(field.fieldName);
                  return (
                    <React.Fragment key={field.fieldName}>
                      <Table.Tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleField(field.fieldName)}
                      >
                        <Table.Td>
                          <Group gap={4}>
                            <ThemeIcon variant="subtle" size="xs" color="gray">
                              {isExpanded ? (
                                <IconChevronDown size={10} />
                              ) : (
                                <IconChevronRight size={10} />
                              )}
                            </ThemeIcon>
                            <Text size="xs" style={{ fontFamily: 'monospace' }}>
                              {field.fieldName}
                            </Text>
                          </Group>
                        </Table.Td>
                        {roles.map((r) => {
                          const visible = field.visibleTo.includes(r.label);
                          return (
                            <Table.Td key={r.label} ta="center">
                              <Tooltip
                                label={visible ? 'Visible' : 'Hidden'}
                              >
                                <ThemeIcon
                                  variant="light"
                                  color={visible ? 'green' : 'red'}
                                  size="sm"
                                >
                                  {visible ? (
                                    <IconEye size={14} />
                                  ) : (
                                    <IconEyeOff size={14} />
                                  )}
                                </ThemeIcon>
                              </Tooltip>
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>
                      {isExpanded && (
                        <Table.Tr>
                          <Table.Td colSpan={roles.length + 1} style={{ background: 'var(--mantine-color-dark-7, #f8f9fa)' }}>
                            <Stack gap={4} p="xs">
                              <Group gap="xs">
                                <IconEye size={12} color="green" />
                                <Text size="xs" fw={500}>Visible to:</Text>
                                {field.visibleTo.map((r) => (
                                  <Badge key={r} size="xs" variant="light" color="green">
                                    {r}
                                  </Badge>
                                ))}
                              </Group>
                              <Group gap="xs">
                                <IconEyeOff size={12} color="red" />
                                <Text size="xs" fw={500}>Hidden from:</Text>
                                {field.hiddenFrom.map((r) => (
                                  <Badge key={r} size="xs" variant="light" color="red">
                                    {r}
                                  </Badge>
                                ))}
                              </Group>
                              <Text size="xs" c="dimmed" mt={2}>
                                Field restricted by Phase 1 (Role) lens — roles without matching scope have this field hidden.
                              </Text>
                            </Stack>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </>
      )}

      {/* ── Universally Hidden ──────────────────────────────────────── */}
      {universallyHidden.length > 0 && (
        <>
          <Divider
            label={
              <Group gap="xs">
                <ThemeIcon variant="light" color="red" size="xs">
                  <IconShieldX size={12} />
                </ThemeIcon>
                <Text size="sm" fw={600}>
                  Universally Hidden ({universallyHidden.length} fields)
                </Text>
              </Group>
            }
            labelPosition="left"
          />
          <Paper p="sm" withBorder>
            <Group gap={4}>
              {universallyHidden.map((f) => (
                <Badge key={f} size="xs" variant="dot" color="red" style={{ fontFamily: 'monospace' }}>
                  {f}
                </Badge>
              ))}
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              These fields are hidden from ALL compared roles by the lens chain.
            </Text>
          </Paper>
        </>
      )}

      {/* ── Universally Visible ─────────────────────────────────────── */}
      {universallyVisible.length > 0 && (
        <>
          <Divider
            label={
              <Group gap="xs">
                <ThemeIcon variant="light" color="green" size="xs">
                  <IconEye size={12} />
                </ThemeIcon>
                <Text size="sm" fw={600}>
                  Universally Visible ({universallyVisible.length} fields)
                </Text>
              </Group>
            }
            labelPosition="left"
          />
          <Paper p="sm" withBorder>
            <Group gap={4}>
              {universallyVisible.map((f) => (
                <Badge key={f} size="xs" variant="dot" color="green" style={{ fontFamily: 'monospace' }}>
                  {f}
                </Badge>
              ))}
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              These fields are visible to ALL compared roles.
            </Text>
          </Paper>
        </>
      )}
    </Stack>
  );
}
